import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEventBus } from 'context/EventBusContext/EventBusContext';
import { EventType } from 'context/EventBusContext/EventBusContext.type';
import { AttioQueryKeys } from 'dashboard409a/common/types/commonTypes';
import { StyledSpanEmptyStatus, StyledStatusChip } from 'dashboard409a/components/ValuationsTable/common/GenericTable.styles';
import { StatusDropdown } from 'dashboard409a/components/ValuationsTable/StatusDropdown';
import { useCellEditEnd } from 'dashboard409a/hooks/useCellEditEnd';
import { AttioStatusOption, SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import type { AttioValuationDetailResponse } from 'dashboard409a/services/shared-types';
import { runValuationStatusTransition } from 'dashboard409a/utilities/ensureValuationStatusChange';
import { Logger } from 'utilities';

interface WorkboardStatusCellProps {
  valuationRow: SimplifiedAttioValuation;
  statusOptions: AttioStatusOption[];
  statusOptionsLoading: boolean;
  onStatusUpdate?: (row: SimplifiedAttioValuation) => void;
  // Injected by ScalarSpreadsheet:
  cell?: unknown;
  disabled?: boolean;
}

const WorkboardStatusCell = ({
  valuationRow,
  statusOptions,
  statusOptionsLoading,
  onStatusUpdate,
}: WorkboardStatusCellProps) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { publish } = useEventBus();
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState<string | null>(null);
  const statusCellRef = useRef<HTMLDivElement>(null);

  const handleStatusEditEnd = useCellEditEnd(statusCellRef, () => setIsEditingStatus(false));
  const displayedStatus = optimisticStatus ?? valuationRow.status;
  const isFinalStatus = displayedStatus === 'Final Sent';

  useEffect(() => {
    if (optimisticStatus && optimisticStatus === valuationRow.status) {
      setOptimisticStatus(null);
    }
  }, [optimisticStatus, valuationRow.status]);

  const handleStatusChange = async (newStatus: string, statusOptionId: string) => {
    const previousStatus = valuationRow.status;
    const previousDetail = queryClient.getQueryData<AttioValuationDetailResponse>([
      AttioQueryKeys.VALUATION_ITEM,
      valuationRow.record_id,
    ]);
    const optimisticRow = { ...valuationRow, status: newStatus };

    try {
      setIsUpdatingStatus(true);
      setOptimisticStatus(newStatus);
      onStatusUpdate?.(optimisticRow);
      queryClient.setQueryData<AttioValuationDetailResponse | undefined>(
        [AttioQueryKeys.VALUATION_ITEM, valuationRow.record_id],
        old => (old ? { ...old, status: newStatus, statusId: statusOptionId } : old)
      );
      publish(EventType.WORKBOARD_VALUATION_UPDATED, {
        valuationId: valuationRow.record_id,
        updates: { status: newStatus },
      });

      const transitionResult = await runValuationStatusTransition({
        valuationId: valuationRow.record_id,
        statusId: statusOptionId,
        statusLabel: newStatus,
        queryClient,
        modalData: undefined,
        companyName: valuationRow.company_name ?? undefined,
      });

      if (!transitionResult.ok) {
        if (transitionResult.reason === 'missing_valuation_date') {
          enqueueSnackbar(
            'Valuation date is missing or invalid. Cannot sync fulfillment.',
            { variant: 'error', autoHideDuration: 10000 }
          );
        } else {
          enqueueSnackbar('Failed to update status. Please try again or contact support.', { variant: 'error' });
        }
        throw new Error(transitionResult.reason);
      }

      publish(EventType.WORKBOARD_VALUATION_UPDATED, {
        valuationId: valuationRow.record_id,
        updates: { status: newStatus },
      });

      setIsEditingStatus(false);
    } catch (error) {
      const rollbackRow = { ...valuationRow, status: previousStatus };
      setOptimisticStatus(previousStatus ?? null);
      onStatusUpdate?.(rollbackRow);
      queryClient.setQueryData([AttioQueryKeys.VALUATION_ITEM, valuationRow.record_id], previousDetail);
      publish(EventType.WORKBOARD_VALUATION_UPDATED, {
        valuationId: valuationRow.record_id,
        updates: { status: previousStatus },
      });
      Logger.error('Error updating status:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isUpdatingStatus && !isFinalStatus) {
      setIsEditingStatus(true);
    }
  };

  let label: string;
  if (isUpdatingStatus) {
    label = 'Updating...';
  } else if (displayedStatus) {
    label = displayedStatus;
  } else {
    label = 'Click to select status';
  }

  return (
    <div ref={statusCellRef} style={{ width: '100%' }}>
      <StatusDropdown
        currentStatus={displayedStatus}
        onStatusChange={handleStatusChange}
        isEditing={isEditingStatus}
        onEditEnd={handleStatusEditEnd}
        statusOptions={statusOptions}
        loading={statusOptionsLoading}
      />
      {!isEditingStatus && (
        displayedStatus ? (
          <StyledStatusChip
            label={label}
            status={displayedStatus}
            onClick={handleStatusClick}
            style={{ cursor: isUpdatingStatus || isFinalStatus ? 'not-allowed' : 'pointer', opacity: isUpdatingStatus ? 0.6 : 1 }}
            title={isFinalStatus ? 'Final status cannot be changed' : undefined}
          />
        ) : (
          <StyledSpanEmptyStatus
            onClick={handleStatusClick}
            style={{ cursor: 'pointer', fontStyle: 'italic' }}>
            {label}
          </StyledSpanEmptyStatus>
        )
      )}
    </div>
  );
};

export default WorkboardStatusCell;
